import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../../styles/lp-variables.css';

/**
 * Mask an email address for display.
 * e.g. "taro@example.com" -> "t***o@example.com"
 */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const masked = local[0] + '***' + (local.length > 1 ? local[local.length - 1] : '');
  return `${masked}@${domain}`;
}

/**
 * CompletePage - Thank-you screen after lead capture
 * Shows confirmation that the report will be delivered via email.
 */
const CompletePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const slug = searchParams.get('slug') || '';

  return (
    <div
      className="landing-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '512px', textAlign: 'center' }}>
        {/* Checkmark Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              margin: '0 auto',
              borderRadius: '50%',
              backgroundColor: 'rgba(62, 178, 152, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              style={{ width: '40px', height: '40px', color: 'var(--lp-accent-green)' }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          style={{
            marginTop: '24px',
            fontSize: '28px',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--lp-text-primary)',
            lineHeight: 1.3,
          }}
        >
          ありがとうございます！
        </motion.h2>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.4 }}
          style={{ marginTop: '16px' }}
        >
          <p
            style={{
              fontSize: '16px',
              color: 'var(--lp-text-secondary)',
              lineHeight: 1.7,
            }}
          >
            バナー生成が完了次第、
            <br />
            メールにてレポートをお届けします。
          </p>
          <p
            style={{
              marginTop: '12px',
              fontSize: '15px',
              color: 'var(--lp-text-secondary)',
            }}
          >
            通常{' '}
            <span style={{ fontWeight: 600, color: 'var(--lp-text-primary)' }}>5~10分</span>{' '}
            ほどで届きます。
          </p>
        </motion.div>

        {/* Masked Email Display */}
        {email && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
            style={{ marginTop: '24px' }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                borderRadius: '12px',
                backgroundColor: 'var(--lp-bg)',
              }}
            >
              <svg
                style={{
                  width: '16px',
                  height: '16px',
                  color: 'var(--lp-text-tertiary)',
                }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span style={{ fontSize: '14px', color: 'var(--lp-text-secondary)' }}>
                送信先:{' '}
                <span style={{ fontWeight: 500, color: 'var(--lp-text-primary)' }}>
                  {maskEmail(email)}
                </span>
              </span>
            </div>
          </motion.div>
        )}

        {/* Spam folder note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          style={{
            marginTop: '24px',
            fontSize: '13px',
            color: 'var(--lp-text-tertiary)',
          }}
        >
          届かない場合は迷惑メールフォルダをご確認ください。
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          style={{ marginTop: '32px' }}
        >
          <button
            onClick={() => navigate('/lp')}
            style={{
              width: '100%',
              maxWidth: '320px',
              height: '52px',
              borderRadius: '9999px',
              backgroundColor: 'var(--lp-accent-green)',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseOver={(e) => {
              (e.target as HTMLButtonElement).style.opacity = '0.9';
            }}
            onMouseOut={(e) => {
              (e.target as HTMLButtonElement).style.opacity = '1';
            }}
          >
            トップページに戻る
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default CompletePage;
